import React, { useEffect, useState, FC, useCallback } from "react";
import { db } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

interface SignatureUploadProps {
  userID: string;
}

interface SignatureUploadState {
  file: File | null;
  uploading: boolean;
  errorMessage: string;
  fileUrl: string;
  fileName: string;
}

const SignatureUpload: FC<SignatureUploadProps> = ({ userID }) => {
  const [state, setState] = useState<SignatureUploadState>({
    file: null,
    uploading: false,
    errorMessage: "",
    fileUrl: "",
    fileName: "",
  });

  const fetchCurrentSignature = useCallback(async () => {
    const storage = getStorage();
    try {
      const userDocRef = doc(db, "users", userID);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.signatureFileName) {
          const fileRef = storageRef(
            storage,
            `signatures/${userID}/${userData.signatureFileName}`
          );
          const url = await getDownloadURL(fileRef);
          setState((prev) => ({
            ...prev,
            fileName: userData.signatureFileName,
            fileUrl: url,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching current signature:", error);
    }
  }, [userID]);

  useEffect(() => {
    fetchCurrentSignature();
  }, [fetchCurrentSignature]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const file = event.target.files[0];
      if (file) {
        if (
          file.type === "image/jpeg" ||
          file.type === "image/jpg" ||
          file.type === "image/png" ||
          file.type === "image/svg+xml"
        ) {
          setState((prev) => ({
            ...prev,
            file,
            fileName: file.name,
            errorMessage: "",
            fileUrl: URL.createObjectURL(file), // Create a preview URL
          }));
        } else {
          setState((prev) => ({
            ...prev,
            errorMessage: "Please upload a JPEG, PNG or SVG file.",
          }));
        }
      }
    }
  };

  const handleUpload = async () => {
    if (state.file) {
      try {
        setState((prev) => ({ ...prev, uploading: true, errorMessage: "" }));
        const storage = getStorage();
        const fileRef = storageRef(
          storage,
          `signatures/${userID}/${state.fileName}`
        );

        await uploadBytes(fileRef, state.file);
        const downloadUrl = await getDownloadURL(fileRef);

        const userDocRef = doc(db, "users", userID);
        await updateDoc(userDocRef, {
          signatureFileName: state.fileName,
        });

        setState((prev) => ({
          ...prev,
          uploading: false,
          fileUrl: downloadUrl,
        }));
        alert("Signature uploaded successfully!");
      } catch (error) {
        console.error("Error uploading the file:", error);
        setState((prev) => ({
          ...prev,
          uploading: false,
          errorMessage: "Failed to upload signature. Please try again.",
        }));
      }
    }
  };

  return (
    <div className="signature-upload" style={{ padding: "1rem" }}>
      <label
        htmlFor="signature-upload"
        className="signature-upload__label"
        style={{ display: "block", fontWeight: "bold" }}
      >
        Upload your signature (JPEG, PNG or SVG format)
      </label>
      <div
        className="signature-upload__description"
        style={{ fontSize: "0.8rem", marginBottom: "1rem" }}
      >
        Personalize your letters with your signature.
      </div>

      <input
        id="signature-upload"
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/svg+xml"
        onChange={handleFileChange}
        disabled={state.uploading}
        aria-describedby="file-upload-error"
      />
      <button
        className="button-small-brand-green signature-upload__button"
        style={{ padding: "0.8rem" }}
        onClick={handleUpload}
        disabled={state.uploading || !state.file || !!state.errorMessage}
        aria-busy={state.uploading}
      >
        {state.uploading ? "Uploading..." : "Upload"}
      </button>
      {state.errorMessage && (
        <div
          id="file-upload-error"
          className="signature-upload__error"
          style={{ color: "red" }}
          role="alert"
        >
          {state.errorMessage}
        </div>
      )}

      {state.fileUrl && (
        <div className="signature-upload__current-signature">
          <br />
          <div style={{ fontSize: "0.8rem", marginBottom: "0.5rem" }}>
            Current signature: {state.fileName}
          </div>
          <img
            src={state.fileUrl}
            alt="Current signature"
            style={{
              maxWidth: "90%",
              objectFit: "contain",
            }}
          />
          <div style={{ fontSize: "0.8rem" }}>
            <a href={state.fileUrl} download>
              Download current signature
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureUpload;
